import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

const checklistPoints = [
  "Letting-agent standard end-of-tenancy checklist",
  "Room-by-room inspection points (with photo prompts)",
  "Deposit-protection tips that actually work",
  "Common landlord deductions to avoid",
];

export default function LeadMagnet() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { email, name, source: "tenant_checklist" });
      setDone(true);
      toast.success("Checklist on the way — check your inbox.");
      setEmail("");
      setName("");
    } catch {
      toast.error("Something went wrong. Try again or call directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="checklist" className="py-24 sm:py-32 bg-[#0B1215] text-[#F8F9FA] grain relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-[#FF5A36] mb-4">· FREE DOWNLOAD</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              The <span className="italic font-light">Move-In</span><br />
              Checklist.
            </h2>
            <p className="mt-6 text-[#F8F9FA]/70 leading-relaxed max-w-xl">
              The exact list I use to prepare a property before a new tenant moves in — the same
              standard letting agents inspect against. Drop your email below and I'll send it
              straight over.
            </p>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              {checklistPoints.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm">
                  <span className="mt-1.5 w-2 h-2 bg-[#FF5A36] shrink-0" />
                  <span className="text-[#F8F9FA]/80">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-5"
            data-testid="lead-magnet"
          >
            {done ? (
              <div className="border-2 border-[#F8F9FA] p-8 bg-[#0B1215]" data-testid="lead-magnet-success">
                <div className="font-mono-label text-[#FF5A36] mb-3">· SENT</div>
                <h3 className="font-display text-2xl tracking-tight mb-3">Check your inbox.</h3>
                <p className="text-sm text-[#F8F9FA]/70">
                  The checklist is on its way. If you don't see it within a few minutes, peek in your
                  spam folder.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="border-2 border-[#F8F9FA]/20 p-8 bg-[#F8F9FA]/[0.03] backdrop-blur-sm space-y-4" data-testid="lead-magnet-form">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#FF5A36] flex items-center justify-center">
                    <Download size={18} />
                  </div>
                  <div className="font-mono-label">Send me the PDF →</div>
                </div>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First name (optional)"
                  className="rounded-none border-[#F8F9FA]/30 bg-transparent text-[#F8F9FA] placeholder:text-[#F8F9FA]/40 h-12"
                  data-testid="lead-name"
                />
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="rounded-none border-[#F8F9FA]/30 bg-transparent text-[#F8F9FA] placeholder:text-[#F8F9FA]/40 h-12"
                  data-testid="lead-email"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5A36] hover:bg-[#E04928] disabled:opacity-60 text-[#F8F9FA] px-8 py-4 font-mono-label active:scale-95 transition-all"
                  data-testid="lead-submit"
                >
                  {loading ? "Sending..." : "Send me the checklist"} <ArrowRight size={16} />
                </button>
                <p className="text-xs text-[#F8F9FA]/40 leading-relaxed">
                  No spam. One email with the PDF. Unsubscribe anytime.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
